/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import graphql from 'graphql-tag';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime';

import { CatchupLastViewedTimeQuery } from '../CatchupLastViewedTimeQuery.graphql';
import type {
	CatchupContentType,
	CatchupLastViewedTimeQuery as CatchupLastViewedTimeQueryResponse,
	CatchupLastViewedTimeQueryVariables,
} from '../__types__/CatchupLastViewedTimeQuery';
import { useAIRovoButtonEnablement } from '../ai-smart-button/useAIRovoButtonEnablement';

import type {
	useDynamicSplitButtonLastModifiedDateQuery as useDynamicSplitButtonLastModifiedDateQuery$data,
	useDynamicSplitButtonLastModifiedDateQueryVariables as useDynamicSplitButtonLastModifiedDateQuery$variables,
} from './__types__/useDynamicSplitButtonLastModifiedDateQuery';
import type {
	useDynamicSplitButtonContentHistoryQuery_Query as useDynamicSplitButtonContentHistoryQueryResponse,
	useDynamicSplitButtonContentHistoryQueryVariables,
} from './__types__/useDynamicSplitButtonContentHistoryQuery';

const useDynamicSplitButtonContentHistoryQuery = graphql`
	query useDynamicSplitButtonContentHistoryQuery($contentId: ID!) {
		contentHistory(contentId: $contentId, first: 1) {
			nodes {
				collaborators {
					userAccountIds
				}
			}
		}
	}
`;

type DynamicSplitButtonReturnType = {
	showCatchupAsDefault: boolean;
	queryLoading: boolean;
	showSummarizeAsDefault?: boolean;
	queryError?: ApolloErrorInterop | undefined;
	readTime?: number;
};

export const useDynamicSplitButton = (
	contentId: string,
	contentType: CatchupContentType,
	isRefreshRovoButton: boolean = false,
): DynamicSplitButtonReturnType => {
	const { isAdminHubAIEnabled, userId } = useSessionData();
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const { readTime, loading: readTimeLoading } = useReadTime(
		contentId,
		!isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	);

	const {
		data,
		loading: catchupLastViewedLoading,
		error: queryError,
	} = useQuery<CatchupLastViewedTimeQueryResponse, CatchupLastViewedTimeQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		CatchupLastViewedTimeQuery,
		{
			variables: {
				contentId,
				contentType,
			},
			skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
		},
	);

	const { data: lastModifiedDateData, loading: lastModifiedLoading } = useQuery<
		useDynamicSplitButtonLastModifiedDateQuery$data,
		useDynamicSplitButtonLastModifiedDateQuery$variables
	>(
		graphql`
			query useDynamicSplitButtonLastModifiedDateQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						metadata {
							lastModifiedDate
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
		},
	);

	const { data: contentHistoryData, loading: contentHistoryLoading } = useQuery<
		useDynamicSplitButtonContentHistoryQueryResponse,
		useDynamicSplitButtonContentHistoryQueryVariables
	>(useDynamicSplitButtonContentHistoryQuery, {
		variables: {
			contentId,
		},
		skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT || !isRovoButtonEnabled,
		fetchPolicy: 'network-only',
	});

	const lastModifiedDate =
		lastModifiedDateData?.content?.nodes?.[0]?.metadata?.lastModifiedDate ?? '';
	const lastViewedTime = data?.catchupGetLastViewedTime?.lastViewedTimeISO ?? '';
	const parsedLastViewedTime = Date.parse(lastViewedTime);
	const parsedLastModifiedDate = Date.parse(lastModifiedDate);
	const lastEditedCollaborators =
		contentHistoryData?.contentHistory?.nodes?.[0]?.collaborators?.userAccountIds ?? [];
	const isCurrentUserARecentCollaborator = lastEditedCollaborators.some(
		(collaborator: string | null) => collaborator === userId,
	);

	const isFirstTimeVisit = Number.isNaN(parsedLastViewedTime);

	const queryLoading =
		catchupLastViewedLoading || lastModifiedLoading || contentHistoryLoading || readTimeLoading;

	if ((!parsedLastViewedTime && !isFirstTimeVisit) || !parsedLastModifiedDate || queryError) {
		return {
			showCatchupAsDefault: false,
			queryLoading,
			queryError,
		};
	}

	if (
		isRovoButtonEnabled &&
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		isFirstTimeVisit &&
		readTime &&
		readTime >= 5 &&
		!isCurrentUserARecentCollaborator
	) {
		if (isRefreshRovoButton && expValEquals('rovo_awareness_experiment', 'cohort', 'test')) {
			return {
				showCatchupAsDefault: false,
				showSummarizeAsDefault: true,
				readTime,
				queryLoading,
			};
		}
	}

	const showCatchupAsDefault =
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		parsedLastModifiedDate > parsedLastViewedTime &&
		!isCurrentUserARecentCollaborator;

	return {
		showCatchupAsDefault,
		showSummarizeAsDefault: false,
		queryLoading,
	};
};
